import type { CreateProductInput, ProductItem } from '@/lib/api/products';

import {
  parsePriceString,
  type SetupManualProductFormData,
} from '@/components/setup/setupManualSchemas';

/** Map existing products → editable form blocks (productId drives submit reconciliation). */
export function mapProductItemsToSetupProductDrafts(
  products: ProductItem[] | undefined,
  currency: string,
): SetupManualProductFormData[] {
  if (!products?.length) return [];

  return products
    .filter((product) => product.active !== false)
    .map((product) => {
      const variants = (product.variants ?? []).map((variant) => {
        const prices = variant.prices ?? [];
        const price =
          prices.find((item) => item.default) ??
          prices.find((item) => item.currencyOptions?.[currency]) ??
          prices[0];
        const amountMinor = price?.currencyOptions?.[currency]?.amount;

        return {
          name: variant.name ?? '',
          imageUrl: variant.imageUrl ?? '',
          sku: variant.sku ?? '',
          priceAmount: amountMinor != null && amountMinor > 0 ? (amountMinor / 100).toFixed(2) : '',
        };
      });

      return {
        productId: product.id,
        name: product.name ?? '',
        description: product.description ?? '',
        variants: variants.length ? variants : [{ name: '', imageUrl: '', sku: '', priceAmount: '' }],
      };
    });
}

/** Map the simplified setup form → product create payload. */
export function toCreateProductInput(
  storeId: string,
  data: SetupManualProductFormData,
  currency: string,
): CreateProductInput {
  return {
    name: data.name,
    description: data.description?.trim() || null,
    unitLabel: null,
    accountingCode: null,
    taxCategory: null,
    mappedTaxCategoryId: null,
    active: true,
    isShippable: true,
    isTaxable: true,
    autoSync: false,
    storeId,
    variants: data.variants.map((variant, index) => ({
      name: variant.name.trim(),
      description: '',
      sku: variant.sku?.trim() || null,
      imageUrl: variant.imageUrl?.trim() || '',
      grams: null,
      active: true,
      default: index === 0,
      price: null,
      compareAtPrice: null,
      prices: [
        {
          recurring: false,
          default: true,
          interval: null,
          intervalCount: null,
          currencyOptions: {
            [currency]: { amount: Math.round((parsePriceString(variant.priceAmount) ?? 0) * 100) },
          },
        },
      ],
    })),
  };
}
